import { supabase } from '../../lib/supabase';
import type { EquipmentRow } from '../../lib/types';
import type { EquipmentCategory, EquipmentClass } from '../../lib/vocab';

const COLUMNS = 'id, name_en, name_he, category, class, photo_path, sort, archived_at, created_at, updated_at';

export async function listEquipment(): Promise<EquipmentRow[]> {
  const { data, error } = await supabase.from('equipment').select(COLUMNS).order('sort', { ascending: true });
  if (error) throw error;
  return (data ?? []) as EquipmentRow[];
}

export async function getEquipment(id: string): Promise<EquipmentRow | null> {
  const { data, error } = await supabase.from('equipment').select(COLUMNS).eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as EquipmentRow) ?? null;
}

/** equipment_id -> number of (non-owner, i.e. library) exercises that link it, for the "used by N exercises" badge and the unused filter. */
export async function getEquipmentUsageCounts(): Promise<Map<string, number>> {
  const { data, error } = await supabase.from('exercise_equipment').select('equipment_id');
  if (error) throw error;
  const counts = new Map<string, number>();
  for (const row of (data ?? []) as { equipment_id: string }[]) {
    counts.set(row.equipment_id, (counts.get(row.equipment_id) ?? 0) + 1);
  }
  return counts;
}

export type EquipmentInput = {
  id: string;
  name_en: string;
  name_he: string;
  category: EquipmentCategory;
  class: EquipmentClass;
  photo_path: string | null;
  sort: number;
};

export async function createEquipment(input: EquipmentInput): Promise<void> {
  const { error } = await supabase.from('equipment').insert({ ...input, updated_at: new Date().toISOString() });
  if (error) throw error;
}

export async function updateEquipment(id: string, patch: Partial<Omit<EquipmentInput, 'id'>>): Promise<void> {
  const { error } = await supabase
    .from('equipment')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function setEquipmentArchived(id: string, archived: boolean): Promise<void> {
  const { error } = await supabase
    .from('equipment')
    .update({ archived_at: archived ? new Date().toISOString() : null, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/** Hard delete. Fails with a foreign-key error (mapped to a friendly message) when the item is in use. */
export async function deleteEquipment(id: string): Promise<void> {
  const { error } = await supabase.from('equipment').delete().eq('id', id);
  if (error) throw error;
}
